import React, { useEffect, useState } from "react";
import { useQuery, useSubscription } from "@apollo/client";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Users,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Clock,
  Home,
  Moon,
  Sun,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sliders,
  Sparkles,
  FileText,
  Building2,
  Umbrella,
  Accessibility,
  Kanban,
  AtSign,
  Mail,
  CalendarRange,
  ClipboardList,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { Tooltip } from "../ui/tooltip";
import {
  GET_UNREAD_CHANGELOG_COUNT,
  GET_ALL_LEAVES,
  GET_SYSTEM_SETTINGS,
  GET_MY_UNREAD_MENTION_COUNT,
  MY_MENTIONS_CHANGED_SUBSCRIPTION,
} from "../../graphql/queries";
import { usePermissions } from "../../hooks/usePermission";

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

// Map menu item IDs to URL paths
const PAGE_PATHS: Record<string, string> = {
  dashboard: "/",
  projects: "/projects",
  employees: "/employees",
  positions: "/positions",
  departments: "/departments",
  shifts: "/shifts",
  events: "/events",
  calendar: "/calendar",
  leaves: "/leaves",
  reports: "/reports",
  devices: "/devices",
  changelog: "/changelog",
  mentions: "/mentions",
  "system-settings": "/system-settings",
  "email-notifications": "/email-notifications",
  "leave-types": "/leave-types",
  "contract-types": "/contract-types",
  "disability-degrees": "/disability-degrees",
  roles: "/roles",
  planning: "/planning",
  "planning-requirements": "/planning/requirements",
};

// Reverse mapping: path -> page id
const PATH_PAGES: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_PATHS).map(([k, v]) => [v, k]),
);

export function getPageFromPath(pathname: string): string {
  return PATH_PAGES[pathname] || "dashboard";
}

export function getPathFromPage(page: string): string {
  return PAGE_PATHS[page] || "/";
}

const TOP_ITEM_IDS = ["dashboard", "projects", "mentions", "changelog"] as const;

const MENU_GROUPS = [
  {
    id: "hr",
    label: "Kadry",
    icon: Users,
    itemIds: ["employees", "positions", "departments", "shifts"],
  },
  {
    id: "worktime",
    label: "Czas pracy",
    icon: Clock,
    itemIds: ["events", "calendar", "leaves", "reports"],
  },
  {
    id: "planning",
    label: "Planowanie",
    icon: CalendarRange,
    itemIds: ["planning", "planning-requirements"],
  },
  {
    id: "config",
    label: "Konfiguracja",
    icon: Sliders,
    itemIds: [
      "system-settings",
      "email-notifications",
      "roles",
      "leave-types",
      "contract-types",
      "disability-degrees",
      "devices",
    ],
  },
] as const;

const EXPANDED_GROUPS_STORAGE_KEY = "workflow-admin:menu-expanded-groups";

function findGroupForPage(pageId: string): string | null {
  for (const g of MENU_GROUPS) {
    if ((g.itemIds as readonly string[]).includes(pageId)) return g.id;
  }
  return null;
}

interface LayoutProps {
  employee: Employee | null;
  onLogout: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ employee, onLogout }) => {
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const currentPage = getPageFromPath(location.pathname);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Fetch unread changelog count
  const { data: unreadData } = useQuery<{ unreadChangelogCount: number }>(
    GET_UNREAD_CHANGELOG_COUNT,
    {
      skip: !employee,
      pollInterval: 60000, // Poll every minute
    },
  );

  const unreadCount = unreadData?.unreadChangelogCount || 0;

  // Unread @mentions for current user, with live updates.
  const { data: mentionsData, refetch: refetchMentions } = useQuery<{
    myUnreadMentionCount: number;
  }>(GET_MY_UNREAD_MENTION_COUNT, {
    skip: !employee,
    pollInterval: 60000,
  });

  useSubscription(MY_MENTIONS_CHANGED_SUBSCRIPTION, {
    variables: { employeeId: employee?.id ?? '' },
    skip: !employee,
    onData: () => {
      refetchMentions().catch(() => {});
    },
    onError: () => {
      /* expected during reconnects */
    },
  });

  const unreadMentionCount = mentionsData?.myUnreadMentionCount || 0;

  const permissions = usePermissions();
  const has = React.useCallback((perm: string) => permissions.includes(perm), [permissions]);

  // Fetch pending leaves count for badge
  const { data: leavesData } = useQuery(GET_ALL_LEAVES, {
    skip: !employee || !has('leaves.view'),
    pollInterval: 120000, // Poll every 2 minutes
    fetchPolicy: 'cache-and-network',
  });

  const { data: settingsData } = useQuery(GET_SYSTEM_SETTINGS, {
    skip: !employee || !has('leaves.view'),
    fetchPolicy: 'cache-first',
  });

  // Only show pending badge if user is a designated approver
  const pendingLeavesCount = React.useMemo(() => {
    if (!leavesData?.leaves || !employee) return 0;
    const pendingCount = leavesData.leaves.filter((l: any) => l.status === 'PENDING').length;

    // Check if current user is a designated approver
    const settings = settingsData?.systemSettings;
    if (settings?.leaveApprovalRequired && settings.leaveApproverIds?.length > 0) {
      if (!settings.leaveApproverIds.includes(employee.id)) return 0;
    }

    return pendingCount;
  }, [leavesData, settingsData, employee]);

  // Each menu item declares the permission slug required to display it.
  // `permission: null` means the item is always visible to authenticated users.
  type MenuItem = {
    id: string;
    label: string;
    icon: any;
    permission: string | null;
    anyOfPermissions?: string[];
    badge?: number;
  };

  const menuItems: MenuItem[] = [
    { id: "dashboard", label: "Dashboard", icon: Home, permission: null },
    { id: "projects", label: "Projekty", icon: Kanban, permission: "projects.view_assigned", anyOfPermissions: ["projects.view_assigned", "projects.view_all"] },
    { id: "employees", label: "Pracownicy", icon: Users, permission: "employees.view" },
    { id: "positions", label: "Stanowiska", icon: Settings, permission: "positions.view" },
    { id: "departments", label: "Działy", icon: Building2, permission: "departments.view" },
    { id: "shifts", label: "Zmianowość", icon: Clock, permission: "shifts.view" },
    { id: "events", label: "Wydarzenia", icon: Clock, permission: "employees.view" },
    { id: "calendar", label: "Kalendarz", icon: Calendar, permission: "leaves.view" },
    { id: "leaves", label: "Nieobecności", icon: Umbrella, permission: "leaves.view", badge: pendingLeavesCount },
    { id: "reports", label: "Raporty", icon: BarChart3, permission: "reports.view" },
    { id: "planning", label: "Plan obsady", icon: CalendarRange, permission: "planning.view" },
    { id: "planning-requirements", label: "Wymagana obsada", icon: ClipboardList, permission: "planning.view" },
    { id: "devices", label: "Urządzenia", icon: Shield, permission: "devices.view" },
    { id: "mentions", label: "Wzmianki", icon: AtSign, permission: null, badge: unreadMentionCount },
    { id: "changelog", label: "Co nowego", icon: Sparkles, permission: null, badge: unreadCount },
    { id: "system-settings", label: "Ogólne", icon: Sliders, permission: "settings.manage" },
    { id: "email-notifications", label: "Powiadomienia mailowe", icon: Mail, permission: "settings.manage" },
    { id: "leave-types", label: "Typy nieobecności", icon: Settings, permission: "leaveTypes.manage" },
    { id: "contract-types", label: "Rodzaje umów", icon: FileText, permission: "contractTypes.manage" },
    { id: "disability-degrees", label: "Stopnie niepełnosprawności", icon: Accessibility, permission: "disabilityDegrees.manage" },
    { id: "roles", label: "Role i uprawnienia", icon: Shield, permission: "roles.manage" },
  ];

  const isItemVisible = (item: MenuItem | undefined): item is MenuItem => {
    if (!employee || !item) return false;
    if (item.anyOfPermissions && item.anyOfPermissions.length > 0) {
      return item.anyOfPermissions.some((p) => has(p));
    }
    return item.permission === null || has(item.permission);
  };

  const itemsById = React.useMemo(() => {
    const map: Record<string, MenuItem> = {};
    for (const item of menuItems) map[item.id] = item;
    return map;
  }, [menuItems]);

  const visibleTopItems = TOP_ITEM_IDS
    .map((id) => itemsById[id])
    .filter(isItemVisible);

  const visibleGroups = MENU_GROUPS.map((g) => ({
    id: g.id,
    label: g.label,
    icon: g.icon,
    items: g.itemIds.map((id) => itemsById[id]).filter(isItemVisible),
  })).filter((g) => g.items.length > 0);

  const flatVisibleItems = [
    ...visibleTopItems,
    ...visibleGroups.flatMap((g) => g.items),
  ];

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    let stored: Record<string, boolean> = {};
    try {
      const raw = localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    const active = findGroupForPage(currentPage);
    if (active) stored[active] = true;
    return stored;
  });

  useEffect(() => {
    const active = findGroupForPage(currentPage);
    if (!active) return;
    setExpandedGroups((prev) => {
      if (prev[active]) return prev;
      const next = { ...prev, [active]: true };
      try {
        localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [currentPage]);

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const renderLeaf = (item: MenuItem, isChild: boolean = false) => {
    const Icon = item.icon;
    const layout = isSidebarCollapsed
      ? "justify-center px-2"
      : isChild
      ? "space-x-3 pl-11 pr-4"
      : "space-x-3 px-4";
    const node = (
      <Link
        to={getPathFromPage(item.id)}
        className={`w-full flex items-center ${layout} py-3 text-left hover:bg-accent/10 dark:hover:bg-accent/20 transition-all no-underline ${
          currentPage === item.id
            ? "bg-primary/10 text-primary border-r-2 border-primary"
            : "text-foreground"
        }`}
      >
        <Icon className="w-5 h-5" />
        {!isSidebarCollapsed && (
          <div className="flex items-center justify-between flex-1">
            <span>{item.label}</span>
            {item.badge && item.badge > 0 && (
              <Badge className="ml-auto bg-primary text-primary-foreground">
                {item.badge}
              </Badge>
            )}
          </div>
        )}
      </Link>
    );
    return isSidebarCollapsed ? (
      <Tooltip key={item.id} content={item.label} side="right">
        {node}
      </Tooltip>
    ) : (
      <React.Fragment key={item.id}>{node}</React.Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-background transition-colors">
      {/* Header - Sticky */}
      <header className="sticky top-0 z-50 bg-background border-b border-border shadow-sm transition-colors">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src="/logo.png"
                  alt="SZKŁO Sp. z o.o."
                  className="h-12 w-auto transition-opacity duration-300 dark:opacity-90"
                />
                <div className="absolute inset-0 bg-background rounded-lg opacity-0 dark:opacity-20 transition-opacity duration-300 -z-10" />
              </div>
              <h1 className="text-2xl font-bold text-foreground transition-colors">
                WorkFlow Admin
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                Witaj, {employee?.firstName} {employee?.lastName}
              </span>

              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="flex items-center space-x-2 hover:bg-accent/10 transition-colors"
              >
                {isDark ? (
                  <Sun className="w-4 h-4 text-yellow-500" />
                ) : (
                  <Moon className="w-4 h-4 text-primary" />
                )}
                <span className="hidden sm:inline">
                  {isDark ? "Jasny" : "Ciemny"}
                </span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Wyloguj</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Sticky & Collapsible */}
          <div
            className={`flex-shrink-0 transition-all duration-300 ${isSidebarCollapsed ? "w-20" : "w-64"}`}
          >
            <div className="sticky top-24">
              <Card>
                <CardHeader className={`${isSidebarCollapsed ? "px-2" : ""}`}>
                  <div
                    className={`flex items-center ${isSidebarCollapsed ? "justify-center" : "justify-between"}`}
                  >
                    {!isSidebarCollapsed && (
                      <div>
                        <CardTitle className="text-lg">Menu</CardTitle>
                        <CardDescription>Zarządzanie systemem</CardDescription>
                      </div>
                    )}
                    <Tooltip
                      content={isSidebarCollapsed ? "Rozwiń menu" : "Zwiń menu"}
                      side="right"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setIsSidebarCollapsed(!isSidebarCollapsed)
                        }
                      >
                        {isSidebarCollapsed ? (
                          <ChevronRight className="w-4 h-4" />
                        ) : (
                          <ChevronLeft className="w-4 h-4" />
                        )}
                      </Button>
                    </Tooltip>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <nav className="space-y-1">
                    {isSidebarCollapsed
                      ? flatVisibleItems.map((item) => renderLeaf(item))
                      : (
                        <>
                          {visibleTopItems.map((item) => renderLeaf(item))}
                          {visibleGroups.map((g) => {
                            const GroupIcon = g.icon;
                            const isOpen = !!expandedGroups[g.id];
                            const hasActiveChild = g.items.some(
                              (i) => i.id === currentPage,
                            );
                            return (
                              <div key={g.id}>
                                <button
                                  type="button"
                                  onClick={() => toggleGroup(g.id)}
                                  aria-expanded={isOpen}
                                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-accent/10 dark:hover:bg-accent/20 transition-all ${
                                    hasActiveChild && !isOpen
                                      ? "text-primary"
                                      : "text-foreground"
                                  }`}
                                >
                                  <GroupIcon className="w-5 h-5" />
                                  <div className="flex items-center justify-between flex-1">
                                    <span className="font-medium">
                                      {g.label}
                                    </span>
                                    <ChevronDown
                                      className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`}
                                    />
                                  </div>
                                </button>
                                {isOpen &&
                                  g.items.map((item) => renderLeaf(item, true))}
                              </div>
                            );
                          })}
                        </>
                      )}
                  </nav>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-muted/50 border-t border-border mt-16">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-muted-foreground">
            <p>
              © 2025-{new Date().getFullYear()} WebCoder Artur Pleskot.
              Wszystkie prawa zastrzeżone.
            </p>
            <p className="mt-1">
              Kontakt:{" "}
              <a
                href="mailto:info@webcoder.pl"
                className="text-primary hover:underline transition-colors"
              >
                info@webcoder.pl
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
